import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaWallet,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaSpinner,
  FaBan,
} from "react-icons/fa";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { usePopup } from "../../context/PopupContext";
import { useTranslation } from "react-i18next";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import Pagination from "../../components/Pagination";
import "./AdminWithdrawalManagement.css";

const PER_PAGE = 10;

const AdminWithdrawalManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);
  const [failModal, setFailModal] = useState({
    open: false,
    withdrawal: null,
    note: "",
  });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchWithdrawals();
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/withdrawals");
      setWithdrawals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
      setWithdrawals([]);
      showToast(t("adminWithdrawalManagement.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredWithdrawals = withdrawals.filter((w) => {
    if (filterStatus === "all") return true;
    return w.status === filterStatus;
  });

  const totalPages = Math.ceil(filteredWithdrawals.length / PER_PAGE);
  const pageWithdrawals = filteredWithdrawals.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  const stats = {
    total: withdrawals.length,
    pending: withdrawals.filter((w) => w.status === "pending").length,
    processing: withdrawals.filter((w) => w.status === "processing").length,
    completed: withdrawals.filter((w) => w.status === "completed").length,
    failed: withdrawals.filter((w) => w.status === "failed").length,
    cancelled: withdrawals.filter((w) => w.status === "cancelled").length,
    totalCompletedAmount: withdrawals
      .filter((w) => w.status === "completed")
      .reduce((sum, w) => sum + (w.amount || 0), 0),
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : "—");

  const handleMarkCompleted = async (w) => {
    setUpdatingId(w._id);
    try {
      await api.patch(`/admin/withdrawals/${w._id}`, { status: "completed" });
      showToast(t("adminWithdrawalManagement.completedSuccess"), "success");
      fetchWithdrawals();
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          t("adminWithdrawalManagement.updateFailed"),
        "error",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const openFailModal = (w) =>
    setFailModal({ open: true, withdrawal: w, note: "" });
  const closeFailModal = () =>
    setFailModal({ open: false, withdrawal: null, note: "" });

  const handleMarkFailed = async () => {
    const { withdrawal, note } = failModal;
    if (!withdrawal) return;
    setUpdatingId(withdrawal._id);
    try {
      await api.patch(`/admin/withdrawals/${withdrawal._id}`, {
        status: "failed",
        note: note.trim() || undefined,
      });
      showToast(t("adminWithdrawalManagement.failedSuccess"), "success");
      closeFailModal();
      fetchWithdrawals();
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          t("adminWithdrawalManagement.updateFailed"),
        "error",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const statusOptions = [
    "all",
    "pending",
    "processing",
    "completed",
    "failed",
    "cancelled",
  ];

  return (
    <AdminLayout>
      <div className="admin-withdrawal-management-content">
        <div className="admin-withdrawal-management-page-header">
          <div>
            <h1 className="admin-withdrawal-management-page-title">
              {t("adminWithdrawalManagement.title")}
            </h1>
            <p className="admin-withdrawal-management-page-subtitle">
              {t("adminWithdrawalManagement.subtitle")}
            </p>
          </div>
        </div>

        <div className="admin-withdrawal-management-stats-cards-grid">
          <div className="admin-withdrawal-management-stat-card">
            <div className="admin-withdrawal-management-stat-icon">
              <FaWallet aria-hidden />
            </div>
            <div className="admin-withdrawal-management-stat-content">
              <div className="admin-withdrawal-management-stat-value">
                {toLocalizedNumber(stats.total)}
              </div>
              <div className="admin-withdrawal-management-stat-label">
                {t("adminWithdrawalManagement.totalRequests")}
              </div>
            </div>
          </div>
          <div className="admin-withdrawal-management-stat-card warning">
            <div className="admin-withdrawal-management-stat-icon">
              <FaClock aria-hidden />
            </div>
            <div className="admin-withdrawal-management-stat-content">
              <div className="admin-withdrawal-management-stat-value">
                {toLocalizedNumber(stats.pending)}
              </div>
              <div className="admin-withdrawal-management-stat-label">
                {t("adminWithdrawalManagement.pending")}
              </div>
            </div>
          </div>
          <div className="admin-withdrawal-management-stat-card success">
            <div className="admin-withdrawal-management-stat-icon">
              <FaCheckCircle aria-hidden />
            </div>
            <div className="admin-withdrawal-management-stat-content">
              <div className="admin-withdrawal-management-stat-value">
                {toLocalizedNumber(stats.completed)}
              </div>
              <div className="admin-withdrawal-management-stat-label">
                {t("adminWithdrawalManagement.completed")}
              </div>
            </div>
          </div>
          <div className="admin-withdrawal-management-stat-card danger">
            <div className="admin-withdrawal-management-stat-icon">
              <FaTimesCircle aria-hidden />
            </div>
            <div className="admin-withdrawal-management-stat-content">
              <div className="admin-withdrawal-management-stat-value">
                {toLocalizedNumber(stats.failed)}
              </div>
              <div className="admin-withdrawal-management-stat-label">
                {t("adminWithdrawalManagement.failed")}
              </div>
            </div>
          </div>
          <div className="admin-withdrawal-management-stat-card highlight">
            <div className="admin-withdrawal-management-stat-icon">
              <FaWallet aria-hidden />
            </div>
            <div className="admin-withdrawal-management-stat-content">
              <div className="admin-withdrawal-management-stat-value">
                Rs. {toLocalizedPrice(stats.totalCompletedAmount)}
              </div>
              <div className="admin-withdrawal-management-stat-label">
                {t("adminWithdrawalManagement.totalProcessed")}
              </div>
            </div>
          </div>
        </div>

        <div className="admin-withdrawal-management-filters-card">
          <div className="admin-withdrawal-management-filter-tabs">
            <span className="admin-withdrawal-management-filter-label">
              Status:
            </span>
            {statusOptions.map((status) => (
              <button
                key={status}
                type="button"
                className={`admin-withdrawal-management-filter-tab ${filterStatus === status ? "active" : ""}`}
                onClick={() => setFilterStatus(status)}
              >
                {status === "all" ? "All" : toLocalizedStatus(status, t)}
                {status === "all"
                  ? ` (${toLocalizedNumber(withdrawals.length)})`
                  : ` (${toLocalizedNumber(withdrawals.filter((w) => w.status === status).length)})`}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-withdrawal-management-table-card">
          {loading ? (
            <div className="admin-withdrawal-management-loading-state">
              <div className="admin-withdrawal-management-spinner" />
              <p>{t("adminWithdrawalManagement.loading")}</p>
            </div>
          ) : filteredWithdrawals.length === 0 ? (
            <div className="admin-withdrawal-management-empty-state">
              <div className="admin-withdrawal-management-empty-icon">
                <FaWallet aria-hidden />
              </div>
              <h3>{t("adminWithdrawalManagement.noWithdrawals")}</h3>
              <p>{t("adminWithdrawalManagement.tryDifferentFilter")}</p>
            </div>
          ) : (
            <div className="admin-withdrawal-management-table-container">
              <table className="admin-withdrawal-management-data-table">
                <thead>
                  <tr>
                    <th>{t("adminWithdrawalManagement.id")}</th>
                    <th>{t("adminWithdrawalManagement.farmer")}</th>
                    <th>{t("adminWithdrawalManagement.amount")}</th>
                    <th>{t("adminWithdrawalManagement.method")}</th>
                    <th>{t("adminWithdrawalManagement.details")}</th>
                    <th>{t("adminWithdrawalManagement.status")}</th>
                    <th>{t("adminWithdrawalManagement.requested")}</th>
                    <th>{t("adminWithdrawalManagement.processed")}</th>
                    <th>{t("adminWithdrawalManagement.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageWithdrawals.map((w) => (
                    <tr key={w._id}>
                      <td className="admin-withdrawal-management-id-cell">
                        #{w._id?.slice(-8) || "N/A"}
                      </td>
                      <td>
                        <div className="admin-withdrawal-management-user-info">
                          <div className="admin-withdrawal-management-user-name">
                            {w.farmer?.name || "N/A"}
                          </div>
                          <div className="admin-withdrawal-management-user-email">
                            {w.farmer?.email || ""}
                          </div>
                        </div>
                      </td>
                      <td className="admin-withdrawal-management-amount">
                        Rs. {toLocalizedPrice(w.amount || 0)}
                      </td>
                      <td>
                        {w.method === "khalti"
                          ? "Khalti"
                          : w.method === "bank"
                            ? "Bank"
                            : "Other"}
                      </td>
                      <td className="admin-withdrawal-management-details-cell">
                        {w.farmerKhaltiId
                          ? `Khalti: ${w.farmerKhaltiId}`
                          : w.farmer?.paymentMethod || "—"}
                      </td>
                      <td>
                        <span
                          className={`admin-withdrawal-management-status-badge admin-withdrawal-management-status-${w.status}`}
                        >
                          {w.status ? toLocalizedStatus(w.status, t) : "—"}
                        </span>
                      </td>
                      <td>{formatDate(w.createdAt)}</td>
                      <td>{formatDate(w.processedAt)}</td>
                      <td className="admin-withdrawal-management-actions-cell">
                        {(w.status === "pending" ||
                          w.status === "processing") && (
                          <>
                            <button
                              type="button"
                              className="admin-withdrawal-management-action-btn complete"
                              disabled={!!updatingId}
                              onClick={() => handleMarkCompleted(w)}
                            >
                              {updatingId === w._id ? (
                                <FaSpinner
                                  className="admin-withdrawal-management-btn-spinner"
                                  aria-hidden
                                />
                              ) : (
                                <FaCheckCircle aria-hidden />
                              )}
                              {t("adminWithdrawalManagement.complete")}
                            </button>
                            <button
                              type="button"
                              className="admin-withdrawal-management-action-btn fail"
                              disabled={!!updatingId}
                              onClick={() => openFailModal(w)}
                            >
                              <FaBan aria-hidden />{" "}
                              {t("adminWithdrawalManagement.fail")}
                            </button>
                          </>
                        )}
                        {w.status !== "pending" &&
                          w.status !== "processing" &&
                          "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Fail modal */}
      {failModal.open && failModal.withdrawal && (
        <div
          className="admin-withdrawal-management-modal-overlay"
          onClick={closeFailModal}
        >
          <div
            className="admin-withdrawal-management-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{t("adminWithdrawalManagement.markFailedTitle")}</h3>
            <p>
              Farmer: <strong>{failModal.withdrawal.farmer?.name}</strong> — Rs.{" "}
              {toLocalizedPrice(failModal.withdrawal.amount || 0)}
            </p>
            <label htmlFor="admin-withdrawal-fail-note">
              {t("adminWithdrawalManagement.reasonLabel")}
            </label>
            <textarea
              id="admin-withdrawal-fail-note"
              className="admin-withdrawal-management-fail-note"
              value={failModal.note}
              onChange={(e) =>
                setFailModal((prev) => ({ ...prev, note: e.target.value }))
              }
              placeholder={t("adminWithdrawalManagement.reasonPlaceholder")}
              rows={3}
            />
            <div className="admin-withdrawal-management-modal-actions">
              <button
                type="button"
                className="admin-withdrawal-management-btn-cancel"
                onClick={closeFailModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-withdrawal-management-btn-fail"
                onClick={handleMarkFailed}
                disabled={!!updatingId}
              >
                {updatingId
                  ? t("adminWithdrawalManagement.updating")
                  : t("adminWithdrawalManagement.markFailed")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminWithdrawalManagement;
